<template>
  <div class="page-container">
    <div class="title-header">
      <div class="title">Kampanya Yönetimi: {{ campaignName }}</div>
      <div class="title-header-buttons">
        <AppButton
          secondary
          xsm
          title="Kampanyadan Ayrıl"
          @buttonClick="handleLeaveCampaign"
        />
      </div>
    </div>

    <!-- Tabs and Filter Container -->
    <div class="content-wrapper">
      <div class="tabs-container">
        <button
          :class="['tab-button', { active: activeTab === 'products' }]"
          @click="handleTabChange('products')"
        >
          <span class="label">Kampanyadaki Ürünler</span>
          <span class="count">({{ productsCount.products }})</span>
        </button>
        <button
          :class="['tab-button', { active: activeTab === 'statistics' }]"
          @click="handleTabChange('statistics')"
        >
          <span class="label">Kampanyaya Ekleyebileceğim Ürünlerim</span>
          <span class="count">({{ productsCount.statistics }})</span>
        </button>
      </div>

      <ManageCampaignFilters
        ref="filtersRef"
        :filters="filters"
        :categories="categories"
        :brands="brands"
        @update:filters="handleFiltersUpdate"
        @apply-filters="applyFilters"
        @clear-filters="clearFilters"
      />
    </div>

    <ManageCampaignTable
      :key="`campaign-in-${manageCampaignTableKey}`"
      v-if="activeTab === 'products'"
      :isLoading="isLoading"
      :products="products"
      :current-page="currentPage"
      :total-pages="totalPages"
      :page-size="pageSize"
      :sort-by="sortBy"
      :show-excel-button="false"
      :show-actions-column="false"
      @page-change="handlePageChange"
      @page-size-change="handlePageSizeChange"
      @sort-change="handleSortChange"
      @selection-change="handleProductSelectionChange"
    />

    <ManageCampaignTable
      :key="`campaign-eligible-${manageCampaignTableKey}`"
      v-if="activeTab === 'statistics'"
      :isLoading="isLoading"
      :products="products"
      :current-page="currentPage"
      :total-pages="totalPages"
      :page-size="pageSize"
      :sort-by="sortBy"
      :show-excel-button="true"
      :show-another-campaign-badge="true"
      @page-change="handlePageChange"
      @page-size-change="handlePageSizeChange"
      @sort-change="handleSortChange"
      @selection-change="handleProductSelectionChange"
      @price-change="handleProductPriceChange"
    />

    <!-- Sticky Footer -->
    <div class="sticky-footer">
      <div class="sticky-footer-content">
        <span v-if="selectedProducts.length > 0" class="selected-count">
          {{ selectedProducts.length }} Ürün Seçildi
        </span>
        <AppButton
          primary
          md
          :title="
            activeTab === 'products' ? 'Kampanyadan Çıkar' : 'Kampanyaya Ekle'
          "
          :disabled="selectedProducts.length === 0"
          @buttonClick="
            activeTab === 'products'
              ? handleRemoveFromCampaign()
              : handleAddToCampaign()
          "
        />
      </div>
    </div>

    <!-- Add to Campaign Modal -->
    <Modal
      ref="addToCampaignModalRef"
      :show-icon="false"
      :modalStyle="{ width: '560px' }"
      positive-action-text="Onayla"
      negative-action-text="Geri Dön"
      :isPositiveActionButtonDisabled="isAddingProducts"
      @on-positive-action-click="handleConfirmAddToCampaign"
      @on-negative-action-click="handleCloseAddToCampaignModal"
      @modal-closed="onAddToCampaignModalClosed"
    >
      <template #body>
        <div class="modal-body-content">
          <Icons name="InfoBlueCircle" color="#4FA4E5" size="lg" />
          <p>
            Kampanyaya eklemek için {{ selectedProducts.length }} ürün seçtiniz.
            Onaylıyor <br />musunuz?
          </p>
        </div>
      </template>
    </Modal>

    <!-- Remove from Campaign Modal -->
    <Modal
      ref="removeFromCampaignModalRef"
      :show-icon="false"
      positive-action-text="Onayla"
      negative-action-text="Geri Dön"
      :isPositiveActionButtonDisabled="isRemovingProducts"
      @on-positive-action-click="handleConfirmRemoveFromCampaign"
      @on-negative-action-click="handleDismissRemoveFromCampaign"
      @modal-closed="handleCloseRemoveFromCampaignModal"
    >
      <template #body>
        <div class="modal-body-content">
          <Icons name="InfoBlueCircle" color="#4FA4E5" size="lg" />
          <p>
            Kampanyadan çıkarmak için {{ selectedProducts.length }} ürün
            seçtiniz. Onaylıyor musunuz?
          </p>
        </div>
      </template>
    </Modal>

    <!-- Failed Products Modal -->
    <FailedProductsModal
      ref="failedProductsModalRef"
      @closed="onFailedProductsModalClosed"
    />

    <!-- Leave Campaign Modal -->
    <Modal
      ref="leaveCampaignModalRef"
      :show-icon="false"
      positive-action-text="Kampanyadan Ayrıl"
      negative-action-text="Vazgeç"
      :isPositiveActionButtonDisabled="isLeavingCampaign"
      @on-positive-action-click="handleConfirmLeaveCampaign"
      @on-negative-action-click="handleCloseLeaveCampaignModal"
      @modal-closed="handleCloseLeaveCampaignModal"
    >
      <template #body>
        <div class="modal-body-content">
          <Icons name="InfoBlueCircle" color="#4FA4E5" size="lg" />
          <p>Kampanyadan ayrılmak istediğinize emin misiniz?</p>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { AppButton, Modal, Icons } from '@n11/so-ui-components';
import { ToastMessageTypes } from '@/utils/enum/toastMessageTypes';
import {
  sendAddProductsCampaignEvent,
  sendAddProductsSuccessEvent,
  sendAddProductsFailEvent,
  sendAddProductsDismissEvent,
  sendButtonClickEvent,
  sendFilterShowResultEvent,
  sendRemoveProductsCampaignEvent,
  sendRemoveProductsSuccessEvent,
  sendRemoveProductsDismissEvent,
  sendRemoveProductsFailEvent,
} from '@/utils/ga4/helper';
import {
  getCampaignDetail,
  getSellerInfo,
  leaveCampaign,
  getCampaignProducts,
  addProductsToCampaign,
  removeProductsFromCampaign,
} from './services/umbrellaCampaignService';
import ManageCampaignFilters from './partials/ManageCampaignFilters.vue';
import ManageCampaignTable from './partials/ManageCampaignTable.vue';
import FailedProductsModal from './partials/FailedProductsModal.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();

const campaignId = ref(route.params.campaignId || null);
const campaignName = ref('');
const campaignData = ref({});
const isLoading = ref(false);

// Route query'den tab bilgisini al, yoksa varsayılan olarak 'products'
const activeTab = ref(
  route.query.tab === 'statistics' ? 'statistics' : 'products',
);
const filters = ref({
  product: '',
  stockAmount: '',
  stockCode: '',
  category: [],
  brand: [],
});
const categories = ref([]);
const brands = ref([]);
const sortBy = ref('');
const currentPage = ref(0);
const totalPages = ref(0);
const pageSize = ref(10);
const products = ref([]);
const productsCount = ref({
  products: 0, // Kampanyadaki ürünler sayısı
  statistics: 0, // Kampanyaya eklenebilecek ürünler sayısı
});
const selectedProducts = ref([]);
const selectedProductPrices = ref({});
const allSelectedProductsMap = ref({});
const manageCampaignTableKey = ref(0);

const filtersRef = ref(null);
const addToCampaignModalRef = ref(null);
const removeFromCampaignModalRef = ref(null);
const leaveCampaignModalRef = ref(null);
const failedProductsModalRef = ref(null);
const pendingFailedProducts = ref(null);
const addToCampaignSucceeded = ref(false);
const isAddingProducts = ref(false);
const isRemovingProducts = ref(false);
const isLeavingCampaign = ref(false);

const fetchCampaignData = async () => {
  if (!campaignId.value) {
    campaignName.value = '';
    return;
  }

  isLoading.value = true;
  try {
    const data = await getCampaignDetail(campaignId.value);
    campaignData.value = data;
    campaignName.value = data.name || '';
  } catch (error) {
    console.error('Error fetching campaign data:', error);
    campaignData.value = {};
    campaignName.value = '';
  } finally {
    isLoading.value = false;
  }
};

const fetchTabCount = async (includedInCampaign) => {
  try {
    const result = await getCampaignProducts(campaignId.value, {
      includedInCampaign,
      page: 0,
      size: 1,
    });
    if (includedInCampaign) {
      productsCount.value.products = result.totalElements || 0;
    } else {
      productsCount.value.statistics = result.totalElements || 0;
    }
  } catch (error) {
    console.error('Error fetching tab count:', error);
  }
};

const fetchProducts = async () => {
  if (!campaignId.value) return;

  isLoading.value = true;
  try {
    // Tab'a göre includedInCampaign parametresini ayarla
    // products tab'ında: kampanyadaki ürünler (includedInCampaign: true)
    // statistics tab'ında: kampanyaya eklenebilecek ürünler (includedInCampaign: false)
    const includedInCampaign = activeTab.value === 'products';

    const stockAmountTrimmed = String(filters.value.stockAmount ?? '').trim();
    const stockAmount = /^\d+$/.test(stockAmountTrimmed)
      ? Number(stockAmountTrimmed)
      : null;

    // Search criteria oluştur
    const searchCriteria = {
      includedInCampaign,
      page: currentPage.value,
      size: pageSize.value,
      ...(filters.value.product &&
        filters.value.product.trim() && {
          productName: filters.value.product.trim(),
        }),
      ...(stockAmount !== null && { stock: stockAmount }),
      ...(filters.value.stockCode &&
        filters.value.stockCode.trim() && {
          sellerStockCode: filters.value.stockCode.trim(),
        }),
      ...(filters.value.category &&
        filters.value.category.length > 0 && {
          categoryIds: filters.value.category.map((c) => c.id),
        }),
      ...(filters.value.brand &&
        filters.value.brand.length > 0 && {
          brandNames: filters.value.brand.map((b) => b.name),
        }),
    };

    const result = await getCampaignProducts(campaignId.value, searchCriteria);

    products.value = result.products || [];
    totalPages.value = result.totalPages || 0;

    // Tab'a göre count'u güncelle
    if (activeTab.value === 'products') {
      productsCount.value.products = result.totalElements || 0;
    } else {
      productsCount.value.statistics = result.totalElements || 0;
    }
  } catch (error) {
    console.error('Error fetching products:', error);
    products.value = [];
    totalPages.value = 0;
    if (activeTab.value === 'products') {
      productsCount.value.products = 0;
    } else {
      productsCount.value.statistics = 0;
    }
  } finally {
    isLoading.value = false;
  }
};

const handleFiltersUpdate = (newFilters) => {
  filters.value = { ...newFilters };
};

const buildFilterAnalyticsPayload = () => {
  const groups = [];

  if (filters.value.product && filters.value.product.trim()) {
    groups.push({
      name: 'Urun Adi',
      values: [filters.value.product.trim()],
    });
  }

  if (filters.value.stockAmount && String(filters.value.stockAmount).trim()) {
    groups.push({
      name: 'Stok Adet',
      values: [String(filters.value.stockAmount).trim()],
    });
  }

  if (filters.value.stockCode && filters.value.stockCode.trim()) {
    groups.push({
      name: 'Stok Kodu',
      values: [filters.value.stockCode.trim()],
    });
  }

  if (filters.value.category && filters.value.category.length > 0) {
    const categoryNames = filters.value.category
      .map((item) => item.name || item.label || '')
      .filter(Boolean);

    if (categoryNames.length > 0) {
      groups.push({
        name: 'Kategori',
        values: categoryNames,
      });
    }
  }

  if (filters.value.brand && filters.value.brand.length > 0) {
    const brandNames = filters.value.brand
      .map((item) => item.name || item.label || '')
      .filter(Boolean);

    if (brandNames.length > 0) {
      groups.push({
        name: 'Marka',
        values: brandNames,
      });
    }
  }

  return {
    filter_count: groups.length,
    selected_filters: groups
      .map((group) => `${group.name}:${group.values.join('|')}`)
      .join(', '),
    filters: groups.map((group) => ({
      filter_name: group.name,
      selected_values: group.values,
      selected_count: group.values.length,
    })),
  };
};

const dismissCampaignActionModals = () => {
  addToCampaignModalRef.value?.hiddenModal();
  removeFromCampaignModalRef.value?.hiddenModal();
};

const resetSelection = () => {
  selectedProducts.value = [];
  allSelectedProductsMap.value = {};
  manageCampaignTableKey.value += 1;
};

const resetPrices = () => {
  selectedProductPrices.value = {};
};

const applyFilters = () => {
  dismissCampaignActionModals();
  currentPage.value = 0;
  resetSelection();
  resetPrices();

  const {
    filter_count,
    selected_filters,
    filters: filtersDetail,
  } = buildFilterAnalyticsPayload();

  if (filter_count > 0) {
    sendFilterShowResultEvent({
      page_type: 'Campaign Detail',
      filter_count,
      selected_filters,
      filters: filtersDetail,
    });
  }

  fetchProducts();
};

const clearFilters = () => {
  dismissCampaignActionModals();
  filters.value = {
    product: '',
    stockAmount: '',
    stockCode: '',
    category: [],
    brand: [],
  };
  currentPage.value = 0;
  resetSelection();
  resetPrices();
  fetchProducts();
};

const handlePageChange = (pageIndex) => {
  currentPage.value = pageIndex;
  fetchProducts();
};

const handlePageSizeChange = (newPageSize) => {
  pageSize.value = Number(newPageSize);
  currentPage.value = 0;
  fetchProducts();
};

const handleSortChange = (selectedSort) => {
  sortBy.value = selectedSort;
  currentPage.value = 0;
  fetchProducts();
};

const handleProductSelectionChange = (selectedIds) => {
  selectedProducts.value = selectedIds;

  // Add current page products that are selected to the map
  products.value.forEach(product => {
    if (selectedIds.includes(product.id)) {
      allSelectedProductsMap.value[String(product.id)] = product;
    }
  });

  // Remove deselected products from the map
  Object.keys(allSelectedProductsMap.value).forEach(key => {
    if (!selectedIds.some(id => String(id) === key)) {
      delete allSelectedProductsMap.value[key];
    }
  });
};

const handleProductPriceChange = (priceData) => {
  selectedProductPrices.value[priceData.id] = priceData;
};

const handleTabChange = (tabName) => {
  if (activeTab.value === tabName) {
    return;
  }

  activeTab.value = tabName;

  sendButtonClickEvent({
    page_path: route.path,
    button_name: 'Kampanyalı Ürünlerim',
    button_type: 'Tab',
    page_type: 'Campaign Detail',
  });
};

const handleAddToCampaign = () => {
  if (selectedProducts.value.length === 0) {
    return;
  }
  addToCampaignSucceeded.value = false;
  if (addToCampaignModalRef.value) {
    addToCampaignModalRef.value.openModal();
  }

  sendAddProductsCampaignEvent({
    button_name: 'Kampanyaya Ekle',
    button_type: 'Add Products to Campaign',
    campaign_id: campaignId.value,
    campaign_name: campaignName.value,
  });
};

const handleCloseAddToCampaignModal = () => {
  if (addToCampaignModalRef.value) {
    addToCampaignModalRef.value.hiddenModal();
  }
};

const onFailedProductsModalClosed = () => {
  resetSelection();
  resetPrices();
  fetchProducts();
  fetchTabCount(true);
};

const onAddToCampaignModalClosed = () => {
  if (pendingFailedProducts.value && pendingFailedProducts.value.length > 0) {
    const items = pendingFailedProducts.value;
    pendingFailedProducts.value = null;
    failedProductsModalRef.value?.openModal(items);
    return;
  }

  if (!addToCampaignSucceeded.value) {
    sendAddProductsDismissEvent({
      button_name: 'Kampanyaya Ekle',
      button_type: 'Add Products to Campaign',
      campaign_id: campaignId.value,
      campaign_name: campaignName.value,
    });
  }
};

const toNumericPrice = (value) => {
  if (typeof value === 'number') return value;
  if (value === null || value === undefined || value === '') return 0;

  const normalizedValue = String(value).replace(/\./g, '').replace(',', '.');
  const parsedValue = Number(normalizedValue);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const handleConfirmAddToCampaign = async () => {
  if (isAddingProducts.value) return;
  isAddingProducts.value = true;

  const selectedProductsData = selectedProducts.value
    .map(id => allSelectedProductsMap.value[String(id)])
    .filter(Boolean);

  const productsPayload = selectedProductsData.map((product) => ({
    productId: product.productId || product.id,
    minPrice: (() => {
      const rawMinPrice =
        selectedProductPrices.value[product.id]?.minPrice ?? product.price;

      if (rawMinPrice === null || rawMinPrice === undefined || rawMinPrice === '') {
        return 0;
      }

      return toNumericPrice(rawMinPrice);
    })(),
    maxPrice:
      selectedProductPrices.value[product.id]?.maxPrice ??
      (product.maxPrice || 0),
  }));

  let result;
  try {
    result = await addProductsToCampaign(campaignId.value, productsPayload);
  } catch {
    isAddingProducts.value = false;
    return;
  }

  const hasFailedProducts =
    result.failedProducts && result.failedProducts.length > 0;

  if (hasFailedProducts) {
    sendAddProductsFailEvent({
      button_name: 'Kampanyaya Ekle',
      button_type: 'Add Products to Campaign',
      campaign_id: campaignId.value,
      campaign_name: campaignName.value,
    });

    pendingFailedProducts.value = result.failedProducts;
    handleCloseAddToCampaignModal();
  } else if (result.success) {
    const itemIds = selectedProductsData.map((p) => p.id).join('|');
    const values = selectedProductsData
      .map((p) => p.n11SalePrice || '')
      .join('|');
    const prices = selectedProductsData
      .map((p) => p.currentPrice || '')
      .join('|');
    const commissions = selectedProductsData
      .map((p) => p.commissionRate || '')
      .join('|');

    sendAddProductsSuccessEvent({
      button_name: 'Onayla',
      button_type: 'Approve',
      campaign_id: campaignId.value,
      campaign_name: campaignName.value,
      item_id: itemIds,
      value: values,
      price: prices,
      commission_rate: commissions,
    });

    store.commit('common/SET_TOASTER_DATA', {
      type: ToastMessageTypes.SUCCESS,
      description: 'Seçtiğiniz ürünler kampanyaya eklendi.',
    });
    addToCampaignSucceeded.value = true;
    handleCloseAddToCampaignModal();
    await nextTick();
    resetSelection();
    resetPrices();
    fetchProducts();
    fetchTabCount(true);
  } else {
    sendAddProductsFailEvent({
      button_name: 'Kampanyaya Ekle',
      button_type: 'Add Products to Campaign',
      campaign_id: campaignId.value,
      campaign_name: campaignName.value,
    });

    handleCloseAddToCampaignModal();
  }

  isAddingProducts.value = false;
};

const handleRemoveFromCampaign = () => {
  if (selectedProducts.value.length === 0) {
    return;
  }
  removeModalDismissed.value = false;
  sendRemoveProductsCampaignEvent({
    button_name: 'Kampanyadan Çıkar',
    button_type: 'Remove Products from Campaign',
    campaign_id: campaignId.value,
    campaign_name: campaignName.value,
  });
  if (removeFromCampaignModalRef.value) {
    removeFromCampaignModalRef.value.openModal();
  }
};

const fireRemoveProductsDismiss = () => {
  const selectedProductsData = selectedProducts.value
    .map(id => allSelectedProductsMap.value[String(id)])
    .filter(Boolean);
  sendRemoveProductsDismissEvent({
    button_name: 'Geri Dön',
    button_type: 'Go Back',
    campaign_id: campaignId.value,
    campaign_name: campaignName.value,
    item_id: selectedProductsData.map((p) => p.id).join('|'),
    value: selectedProductsData.map((p) => p.n11SalePrice || '').join('|'),
    price: selectedProductsData.map((p) => p.currentPrice || '').join('|'),
    commission_rate: selectedProductsData
      .map((p) => p.commissionRate || '')
      .join('|'),
  });
};

const removeModalDismissed = ref(false);

const handleDismissRemoveFromCampaign = () => {
  if (!removeModalDismissed.value) {
    removeModalDismissed.value = true;
    fireRemoveProductsDismiss();
  }
  if (removeFromCampaignModalRef.value) {
    removeFromCampaignModalRef.value.hiddenModal();
  }
};

const handleCloseRemoveFromCampaignModal = () => {
  removeModalDismissed.value = false;
  isRemovingProducts.value = false;
};

const handleConfirmRemoveFromCampaign = async () => {
  if (isRemovingProducts.value) {
    return;
  }

  isRemovingProducts.value = true;

  try {
    const productIds = selectedProducts.value
      .map((selectedId) => {
        const product = allSelectedProductsMap.value[String(selectedId)];
        return product?.productId ?? selectedId;
      })
      .filter((id) => id !== undefined && id !== null)
      .map((id) => Number(id));

    const result = await removeProductsFromCampaign(
      campaignId.value,
      productIds,
    );

    if (result.success) {
      store.commit('common/SET_TOASTER_DATA', {
        type: ToastMessageTypes.SUCCESS,
        description: 'Seçtiğiniz ürünler kampanyadan çıkarıldı.',
      });
      sendRemoveProductsSuccessEvent({
        button_name: 'Onayla',
        button_type: 'Approve',
        campaign_id: campaignId.value,
        campaign_name: campaignName.value,
        item_id: selectedProducts.value.join('|'),
      });
      removeFromCampaignModalRef.value?.hiddenModal();
      await nextTick();
      resetSelection();
      await fetchProducts();
      fetchTabCount(false);
    } else {
      const selectedProductsData = selectedProducts.value
        .map(id => allSelectedProductsMap.value[String(id)])
        .filter(Boolean);
      sendRemoveProductsFailEvent({
        campaign_id: campaignId.value,
        campaign_name: campaignName.value,
        item_id: selectedProductsData.map((p) => p.id).join('|'),
        value: selectedProductsData.map((p) => p.n11SalePrice || '').join('|'),
        price: selectedProductsData.map((p) => p.currentPrice || '').join('|'),
        commission_rate: selectedProductsData
          .map((p) => p.commissionRate || '')
          .join('|'),
      });
      removeFromCampaignModalRef.value?.hiddenModal();
      await nextTick();
      resetSelection();
      await fetchProducts();
      fetchTabCount(false);
    }
  } finally {
    isRemovingProducts.value = false;
  }
};

const handleLeaveCampaign = () => {
  if (leaveCampaignModalRef.value) {
    leaveCampaignModalRef.value.openModal();
  }
};

const handleCloseLeaveCampaignModal = () => {
  if (leaveCampaignModalRef.value) {
    leaveCampaignModalRef.value.hiddenModal();
  }
  isLeavingCampaign.value = false;
};

const handleConfirmLeaveCampaign = async () => {
  if (isLeavingCampaign.value) return;
  isLeavingCampaign.value = true;

  const result = await leaveCampaign(campaignId.value);

  if (result.success) {
    sessionStorage.setItem(
      'leaveCampaignToast',
      JSON.stringify({
        type: ToastMessageTypes.SUCCESS,
        description: 'İşleminiz başlatıldı. Kampanyadaki ürün sayısına bağlı olarak tamamlanması biraz zaman alabilir.',
      }),
    );
    router.push('/campaigns/umbrella');
  }

  isLeavingCampaign.value = false;
};

watch(activeTab, () => {
  dismissCampaignActionModals();
  currentPage.value = 0;
  resetSelection();
  resetPrices();
  fetchProducts();
});

// Route query değiştiğinde tab'ı güncelle
watch(
  () => route.query.tab,
  (newTab) => {
    if (newTab === 'statistics') {
      activeTab.value = 'statistics';
    } else if (newTab === 'products' || !newTab) {
      activeTab.value = 'products';
    }
  },
);

onMounted(async () => {
  if (route.params.campaignId) {
    campaignId.value = route.params.campaignId;
  }

  // Seller info API'den categories ve brands'i al
  try {
    const sellerInfo = await getSellerInfo(campaignId.value);
    categories.value = sellerInfo.categories || [];
    brands.value = sellerInfo.brands || [];
  } catch (error) {
    console.error('Error fetching seller info:', error);
  }

  // Kampanya detayını ve ürünleri yükle
  await fetchCampaignData();
  await Promise.all([
    fetchProducts(),
    fetchTabCount(activeTab.value !== 'products'),
  ]);
});
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables';

.page-container {
  display: flex;
  width: 100%;
  flex-direction: column;

  .title-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0;

    .title {
      color: $colorN80;
      font-size: 20px;
      font-weight: 700;
      letter-spacing: -0.2px;
      line-height: 26px;
    }
  }

  .content-wrapper {
    background: $colorN00;
    border-radius: 8px;
    border-bottom: 0.5px solid $colorN30;
    display: flex;
    flex-direction: column;

    .tabs-container {
      display: flex;
      gap: 12px;
      padding: 16px 24px;
      border-bottom: 0.5px solid $colorN30;

      .tab-button {
        flex: 1;
        min-height: 64px;
        padding: 12px 24px;
        border: 0.5px solid $colorD30;
        border-radius: 6px;
        background: $colorN00;
        color: $colorN80;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 4px;

        .label {
          color: $colorN80;
          text-align: center;
          font-size: 14px;
          font-weight: 700;
          line-height: 18px;
          letter-spacing: -0.05px;
        }

        .count {
          color: $colorN60;
          text-align: center;
          font-size: 14px;
          font-weight: 700;
          line-height: 18px;
          letter-spacing: -0.05px;
        }

        &.active {
          background: $colorM05;
          border: 2px solid $colorM50;
          color: $colorN80;
          font-weight: 600;

          .label {
            color: $colorN80;
          }

          .count {
            color: $colorN80;
          }
        }
      }
    }
  }

  :deep(.popover-wrapper__content) {
    max-width: 250px !important;
    width: auto !important;
    transform: translateX(-15%) !important;
    position: relative;
    z-index: 9999 !important;
  }

  :deep(.popover-wrapper),
  :deep(.popover),
  :deep(.popover-content),
  :deep(.popover__content) {
    z-index: 9999 !important;
  }

  .statistics-content {
    background: $colorN00;
    border-radius: 8px;
    padding: 24px;
    margin-top: 16px;
    margin-bottom: 100px;

    .placeholder-content {
      padding: 40px;
      text-align: center;
      color: $colorN80;
      font-size: 14px;
    }
  }
}

.sticky-footer {
  position: fixed;
  z-index: 10;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  padding: 20px 200px;
  border-top: 0.5px solid $colorN30;
  background: $colorN00;

  .sticky-footer-content {
    display: flex;
    align-items: center;
    gap: 16px;

    .selected-count {
      color: $colorN80;
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      letter-spacing: -0.05px;
    }
  }

  :deep(button) {
    width: 193px;
  }
}

.modal-body-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  :deep(.icon) {
    width: 60px;
    height: 60px;

    svg {
      width: 60px !important;
      height: 60px !important;
    }
  }

  p {
    margin: 0;
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    letter-spacing: -0.02px;
    text-align: center;
  }
}
</style>
